<script setup>
import { Gift } from 'lucide-vue-next'
import useSeoHelper from '~/composables/useSeoHelper'
import { GtmItemListId } from '~/composables/useKftGtm/types'

definePageMeta({
  layout: 'partner',
  allowOnPartnerPage: true,
})

const { data: partnerData } = await useSubdomainData()
const { t } = useI18n()
const localePath = useLocalePath()

// Add error handling for when partnerData is not available
if (!partnerData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

// Use computed for partnerID to ensure reactivity
const partnerID = computed(() => {
  return String(supplierGetters.getId(partnerData.value || {}))
})

// Use the original useFetchSupplierEvents with key and watch options
const { data: partnerProducts = [] } = await useFetchSupplierEvents(
  partnerID.value,
  null,
  {
    key: `subdomain-partner-products-${partnerID.value}`,
    watch: [partnerID],
    transform: (data) => data?.data || {},
    default: () => ({}),
  }
)

const shouldEnableGiftcard = supplierGetters.getHasGiftcardEmbedded(
  partnerData.value
)

const promoPosition = computed(() =>
  Math.min(2, (partnerProducts.value?.length ?? 0) - 1)
)

const seoOptions = computed(() => ({
  head: {
    title: supplierGetters.getName(partnerData.value),
  },
  shouldBeIndexed: false,

  meta: {
    title:
      supplierGetters.getName(partnerData.value) +
      ' | ' +
      t('general.titles.base'),
    description:
      supplierGetters.getName(partnerData.value) +
      ': ' +
      t('general.descriptions.sub-domain'),
    'og:description':
      supplierGetters.getName(partnerData.value) +
      ': ' +
      t('general.descriptions.sub-domain'),
  },
}))

useSeoHelper(seoOptions)
</script>

<template>
  <div class="max-w-screen-xl w-full m-auto px-4 mt-10">
    <ProductShowcase
      :products="partnerProducts"
      :tags="[]"
      :promotion-slot-indices="[promoPosition]"
      :title="
        $t('supplierProfile.eventsFromTheSameSupplier', {
          supplierName: supplierGetters.getName(partnerData),
        })
      "
      :title-h-class="'text-h4'"
      :title-level="3"
      :gtm-item-list-id="GtmItemListId.SubdomainPage"
    >
      <template #promotion1>
        <PromoProductCard
          v-if="shouldEnableGiftcard"
          color="red"
          :icon="Gift"
          :badge-label="t('general.promotionCards.giftcard.badge')"
          :title="t('general.promotionCards.giftcard.title')"
          :subtitle="
            t('general.promotionCards.bookingSolutionGiftcard.subtitle')
          "
          :cta-label="t('general.promotionCards.giftcard.CTALabel')"
          :link="localePath('/giftcard')"
          :image="
            partnerData?.booking_solution_settings?.data
              ?.gift_card_product_image?.data?.url ||
            '/_nuxt3-static/promo-cards/giftbox.webp'
          "
        />
      </template>
    </ProductShowcase>
  </div>
</template>
